// components/SegmentsSearchInterface.jsx - Updated with no auto-search on logic change

import {
	Download,
	Filter,
	Grid,
	List,
	RefreshCw,
	Search,
	Settings,
	SlidersHorizontal,
	X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { buildScoringTiers } from "../utils";

// Tier thresholds come from the recommended-threshold sheet (Segments column).
const scoringTiers = buildScoringTiers("SEGMENTS", [
	{
		key: "bestMatch",
		label: "Best Match",
		meaning: "The most accurate results.",
		warning: false,
	},
	{
		key: "strongMatch",
		label: "Strong Match",
		meaning: "Very relevant results.",
		warning: false,
	},
	{
		key: "acceptable",
		label: "Acceptable",
		meaning: "Results are generally relevant.",
		warning: false,
	},
	{
		key: "extended",
		label: "Extended",
		meaning: "Includes less relevant results.",
		warning: true,
	},
	{
		key: "maximumReach",
		label: "Maximum Reach",
		meaning: "Shows all qualifying results; many may have low relevance.",
		warning: true,
	},
]);

export const SegmentsSearchInterface = ({
	// Props from unified hook
	searchQuery,
	onSearchInputChange,
	searchTerms,
	addSearchTerm,
	removeSearchTerm,
	logicChain,
	onLogicChainChange,
	onSearch,
	isSearching,
	searchResults,
	error,
	filters,
	updateFilter,

	// Additional props
	onExport,
	onRefresh,
	resultsCount = 0,
	sortBy = "compositeScore",
	setSortBy,
	viewMode = "grid",
	setViewMode,
	isFilterSidebarOpen,
	onToggleFilterSidebar,
	activeSubTab = "OA",
	onSubTabChange,
}) => {
	const [selectedTier, setSelectedTier] = useState(scoringTiers[1]); // Default to "Strong Match"

	// TierDropdown component
	const TierDropdown = ({ selectedTier, onTierChange, scoringTiers }) => {
		const [showTooltip, setShowTooltip] = useState(false);

		const handleTierSelect = (e) => {
			const tierLabel = e.target.value;
			const tier = scoringTiers.find((t) => t.label === tierLabel);
			onTierChange(tier);
		};

		return (
			<div className="tier-dropdown-container">
				<div className="tier-dropdown-wrapper">
					<select
						value={selectedTier.label}
						onChange={handleTierSelect}
						className="tier-simple-select"
					>
						{scoringTiers.map((tier) => (
							<option key={tier.label} value={tier.label}>
								{tier.label}
							</option>
						))}
					</select>

					<div
						className="info-icon-container"
						onMouseEnter={() => setShowTooltip(true)}
						onMouseLeave={() => setShowTooltip(false)}
					>
						<span className="info-icon">i</span>

						{showTooltip && (
							<div className="tier-tooltip">
								<div className="tooltip-header">
									<strong>Scoring Tiers</strong>
								</div>
								<div className="tooltip-content">
									{scoringTiers.map((tier, index) => (
										<div
											key={index}
											className={`tooltip-tier ${tier.warning ? "warning" : ""}`}
										>
											<div className="tooltip-tier-header">
												<span className="tooltip-tier-label">{tier.label}</span>
												<span className="tooltip-tier-score">
													{tier.score}+
												</span>
											</div>
											<div className="tooltip-tier-meaning">
												{tier.warning && (
													<span className="warning-indicator">⚠️ </span>
												)}
												{tier.meaning}
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	};

	const sortOptions = [
		{ value: "sv.segment_id", label: "Segment ID" },
		{ value: "reach", label: "Reach (High to Low)" },
		{ value: "reach_asc", label: "Reach (Low to High)" },
		{ value: "avgEcpm", label: "eCPM (High to Low)" },
		{ value: "avgEcpm_asc", label: "eCPM (Low to High)" },
		{ value: "segment_name", label: "Name (A-Z)" },
		{ value: "updated", label: "Recently Updated" },
	];

	// Enhanced logic chain management - include ALL terms (include and exclude)
	useEffect(() => {
		if (searchTerms.length > 0) {
			const newLogic = searchTerms.map((term, index) => {
				// For exclude terms, always use NOT operator (or no operator if it's the last term)
				// For include terms, use OR by default (or no operator if it's the last term)
				const isLastTerm = index === searchTerms.length - 1;
				let operator = null;

				if (!isLastTerm) {
					operator = term.type === "exclude" ? "NOT" : "OR";
				}

				return {
					term: term,
					operator: operator,
				};
			});
			onLogicChainChange(newLogic);
		} else {
			onLogicChainChange([]);
		}
	}, [searchTerms, onLogicChainChange]);

	const handleKeyPress = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleAddTermDirect("include");
		}
	};

	const handleSearchClick = () => {
		console.log("🔍 Search clicked - triggering unified search");

		// Update sort settings in filters if setSortBy is available
		if (setSortBy && sortBy !== filters.sortBy) {
			updateFilter("sortBy", sortBy);
		}

		// Send ALL terms to backend (both include and exclude)
		// Backend will return results for all terms
		// But we'll filter the UI display to show only include terms
		console.log("🔍 Sending all search terms to backend:", {
			searchTerms: searchTerms.map((t) => ({
				text: t.text,
				type: t.type,
				score: t.score,
			})),
			logicChain,
		});

		// Trigger search with current state - backend gets everything
		onSearch();
	};

	const handleAddTermDirect = (termType) => {
		if (searchQuery && searchQuery.trim()) {
			addSearchTerm(
				termType,
				searchQuery.trim(),
				selectedTier.score,
				selectedTier.threshold,
			);
			onSearchInputChange(""); // Clear input after adding term
		}
	};

	const handleClearAll = () => {
		// Clear search terms - this will be handled by the parent component
		searchTerms.forEach((term) => removeSearchTerm(term.id));
		onSearchInputChange("");
		onLogicChainChange([]);
	};

	// 🚫 REMOVED: Auto-search on logic change
	// Enhanced logic chain handlers - NO AUTO SEARCH
	const toggleOperator = (index) => {
		const newLogic = [...logicChain];
		if (newLogic[index] && newLogic[index].operator) {
			const currentOperator = newLogic[index].operator;
			const term = newLogic[index].term;

			// Cycle through: OR -> AND -> NOT -> OR...
			let newOperator;
			let newTermType = term.type;

			if (currentOperator === "OR") {
				newOperator = "AND";
				newTermType = "include";
			} else if (currentOperator === "AND") {
				newOperator = "NOT";
				newTermType = "exclude";
			} else if (currentOperator === "NOT") {
				newOperator = "OR";
				newTermType = "include";
			}

			// Update the operator in the logic chain
			newLogic[index].operator = newOperator;

			// Update the term type if it changed
			if (newTermType !== term.type) {
				// Remove the old term and add as new type
				removeSearchTerm(term.id);
				// Small delay to allow state to update before adding new term
				setTimeout(() => {
					addSearchTerm(newTermType, term.text, term.score);
				}, 50);
			} else {
				onLogicChainChange(newLogic);
			}

			// 🚫 REMOVED: Automatic search trigger
			// User must manually click search button to execute the new logic
		}
	};

	// 🚫 REMOVED: removeTermFromLogic function since we don't want term removal from logic chain

	// Helper function to get the next operator in cycle
	const getNextOperator = (currentOperator) => {
		if (currentOperator === "OR") return "AND";
		if (currentOperator === "AND") return "NOT";
		return "OR";
	};

	// Separate include and exclude terms for display
	const includeTerms = searchTerms.filter((term) => term.type === "include");
	const excludeTerms = searchTerms.filter((term) => term.type === "exclude");

	const formatDemographicLocation = (locationObj) => {
		if (!locationObj || !locationObj.type || !locationObj.selections?.length)
			return null;
		const typeLabel =
			locationObj.type === "dma"
				? "DMA"
				: locationObj.type === "congressional"
					? "Congressional"
					: "State";
		return `${typeLabel}: ${locationObj.selections.length} selected`;
	};

	// Helper function to format age range
	const formatAgeRange = (ageObj) => {
		if (!ageObj || (!ageObj.start && !ageObj.end)) return null;
		const start = ageObj.start || "0";
		const end = ageObj.end || "120";
		return `Age: ${start}-${end}`;
	};

	const formatArrayDisplay = (array, maxLength = 60, maxItems = 3) => {
		if (!array || array.length === 0) return null;

		// If only one item, show it fully
		if (array.length === 1) {
			return array[0];
		}

		// Try to show as many items as possible within the character limit
		let result = array[0];
		let itemCount = 1;

		for (let i = 1; i < array.length && itemCount < maxItems; i++) {
			const nextItem = array[i];
			const testResult = result + ", " + nextItem;

			if (testResult.length <= maxLength) {
				result = testResult;
				itemCount++;
			} else {
				break;
			}
		}

		// If we couldn't fit all items, add count indicator
		if (itemCount < array.length) {
			const remaining = array.length - itemCount;
			result += ` (+${remaining} more)`;
		}

		return result;
	};

	return (
		<div className="search-interface-container">
			{/* Search Terms Display */}
			{searchTerms.length > 0 && (
				<div className="search-terms-display">
					<div className="search-terms-content">
						<div className="current-search-term">
							<span className="font-medium text-gray-700">
								Current search:{" "}
							</span>

							{searchTerms.length > 0 ? (
								searchTerms.map((term, index) => (
									<span key={term.id}>
										<span
											className={`search-term ${term.type === "include" ? "term-include" : "term-exclude"}`}
										>
											{term.type === "include" ? "+" : "-"}
											{term.text} ({term.score})
											<button
												onClick={() => removeSearchTerm(term.id)}
												className="term-remove-btn"
											>
												<X className="w-3 h-3" />
											</button>
										</span>
										{index < searchTerms.length - 1 && <span> </span>}
									</span>
								))
							) : (
								<span className="search-term term-include">
									+{searchQuery} (
									{searchResults?.totalRecords ?? resultsCount ?? 0})
								</span>
							)}
						</div>
					</div>
					<button className="btn-clear-all-inline" onClick={handleClearAll}>
						Clear All
					</button>
				</div>
			)}

			{/* Main Search Row */}
			<div className="search-input-row">
				<div className="search-input-wrapper">
					<div className="search-icon-container">
						<Search size={16} />
					</div>
					<input
						type="text"
						placeholder="Search segments by name, description, attributes, or segment ID..."
						value={searchQuery}
						onChange={(e) => onSearchInputChange(e.target.value)}
						onKeyPress={handleKeyPress}
						className="search-input"
					/>

					<div className="term-type-buttons">
						<button
							type="button"
							onClick={() => handleAddTermDirect("include")}
							className="term-type-btn include-btn"
							disabled={!searchQuery || !searchQuery.trim()}
						>
							Include
						</button>
						<button
							type="button"
							onClick={() => handleAddTermDirect("exclude")}
							className="term-type-btn exclude-btn"
							disabled={!searchQuery || !searchQuery.trim()}
						>
							Exclude
						</button>
					</div>
				</div>

				{/* Tier Dropdown */}
				<div className="tier-input-container">
					<TierDropdown
						selectedTier={selectedTier}
						onTierChange={(tier) => setSelectedTier(tier)}
						scoringTiers={scoringTiers}
					/>
				</div>

				<div className="search-button-container">
					<button
						onClick={handleSearchClick}
						disabled={isSearching}
						className="search-button"
					>
						{isSearching ? (
							<>
								<div className="loading-spinner-small"></div>
								Searching...
							</>
						) : (
							<>Search</>
						)}
					</button>
				</div>
			</div>

			{/* Enhanced Logic Builder - Show when there are terms - NO REMOVE BUTTONS */}
			{logicChain.length > 0 && (
				<div className="logic-builder">
					<div className="logic-builder-header">
						<h4>Search Logic</h4>
						<span className="logic-helper">
							Click AND/OR/NOT to change logic between terms. Click Search to
							apply changes.
						</span>
					</div>
					<div className="logic-chain">
						{logicChain.map((item, index) => (
							<div key={index} className="logic-item-group">
								<div
									className={`logic-term-button ${item.term.type === "exclude" ? "exclude-term" : "include-term"}`}
								>
									<span className="term-prefix">
										{item.term.type === "include" ? "+" : "−"}
									</span>
									<span className="term-text">{item.term.text}</span>
									<span className="term-score">({item.term.score})</span>
									{/* 🚫 REMOVED: Remove button */}
								</div>

								{item.operator && (
									<button
										onClick={() => toggleOperator(index)}
										className={`logic-operator ${item.operator.toLowerCase()}`}
										title={`Click to change to ${getNextOperator(item.operator)}`}
									>
										{item.operator}
									</button>
								)}
							</div>
						))}
					</div>
				</div>
			)}

			{/* Quick Filters Display */}
			<div className="quick-filters">
				{/* Sort Filter */}
				<span className="quick-filter filter-basic">
					Sort: {filters.sortBy || "compositeScore"}
				</span>

				{/* Demographics Filters */}
				{filters.demographicAge &&
					(filters.demographicAge.start || filters.demographicAge.end) && (
						<span className="quick-filter filter-demographic">
							{formatAgeRange(filters.demographicAge)}
						</span>
					)}

				{filters.demographicGender && (
					<span className="quick-filter filter-demographic">
						Gender: {filters.demographicGender === "Male" ? "Male" : "Female"}
					</span>
				)}

				{filters.demographicLocation &&
					formatDemographicLocation(filters.demographicLocation) && (
						<span className="quick-filter filter-demographic">
							Location: {formatDemographicLocation(filters.demographicLocation)}
						</span>
					)}

				{/* Basic Filters */}
				{filters.accountName?.length > 0 && (
					<span className="quick-filter filter-basic">
						Accounts: {formatArrayDisplay(filters.accountName)}
					</span>
				)}

				{filters.type?.length > 0 && (
					<span className="quick-filter filter-basic">
						Type: {formatArrayDisplay(filters.type, 40)}
					</span>
				)}

				{filters.audienceType?.length > 0 && (
					<span className="quick-filter filter-basic">
						Audience: {formatArrayDisplay(filters.audienceType, 40)}
					</span>
				)}

				{/* Category Filters */}
				{filters.categoryL1?.length > 0 && (
					<span className="quick-filter filter-category">
						Categories L1: {formatArrayDisplay(filters.categoryL1)}
					</span>
				)}

				{filters.categoryL2?.length > 0 && (
					<span className="quick-filter filter-category">
						Categories L2: {formatArrayDisplay(filters.categoryL2)}
					</span>
				)}

				{filters.subType?.length > 0 && (
					<span className="quick-filter filter-category">
						Sub Types: {formatArrayDisplay(filters.subType)}
					</span>
				)}

				{/* Data Source Filters */}
				{filters.source?.length > 0 && (
					<div className="filter-group">
						{filters.source.map((source) => (
							<span key={source} className="quick-filter filter-source">
								Source: {source}
							</span>
						))}
					</div>
				)}

				{filters.providerId?.length > 0 && (
					<span className="quick-filter filter-source">
						Providers: {formatArrayDisplay(filters.providerId)}
					</span>
				)}

				{filters.allowedCountries?.length > 0 && (
					<div className="filter-group">
						{filters.allowedCountries.map((country) => (
							<span key={country} className="quick-filter filter-source">
								Country: {country.replace(/[[\]"]/g, "")}
							</span>
						))}
					</div>
				)}

				{filters.status?.length > 0 && (
					<span className="quick-filter filter-qualifier">
						Status: {formatArrayDisplay(filters.status)}
					</span>
				)}

				{/* Value Qualifiers */}
				{filters.valueQualifier?.length > 0 && (
					<span className="quick-filter filter-qualifier">
						Qualifiers: {formatArrayDisplay(filters.valueQualifier)}
					</span>
				)}

				{/* Metrics Filters (if you have them) */}
				{filters.reach > 0 && (
					<span className="quick-filter filter-metrics">
						Min Reach: {filters.reach.toLocaleString()}
					</span>
				)}

				{filters.weightedImpressions > 0 && (
					<span className="quick-filter filter-metrics">
						Min Impressions:{" "}
						{filters.weightedImpressions.toLocaleString()}
					</span>
				)}

				{filters.weightedBidCpm > 0 && (
					<span className="quick-filter filter-metrics">
						Min Bid CPM:{" "}
						{filters.weightedBidCpm.toLocaleString()}
					</span>
				)}

				{filters.avgEcpm > 0 && (
					<span className="quick-filter filter-metrics">
						Min eCPM: ${filters.avgEcpm.toFixed(2)}
					</span>
				)}
			</div>

			{searchResults?.demographicProbabilities && (
				<div className="demographic-probabilities-indicator">
					<span className="tag tag-blue">Demographic Match</span>
					<span className="demographic-probability-text">
						{Object.entries(searchResults.demographicProbabilities).map(
							([key, probability]) => (
								<span key={key}>
									{key.replace("_", " ")}: {(probability * 100).toFixed(1)}%
									confidence
								</span>
							),
						)}
					</span>
				</div>
			)}

			{searchResults?.searchMetadata && (
				<div className="search-metadata-display">
					<div className="metadata-items">
						<span className="metadata-item">
							Query:{" "}
							{Array.isArray(searchResults.searchMetadata.query)
								? searchResults.searchMetadata.query.join(", ")
								: searchResults.searchMetadata.query}
						</span>
						<span className="metadata-item">
							Search time:{" "}
							{new Date(
								searchResults.searchMetadata.timestamp,
							).toLocaleTimeString()}
						</span>
						{searchResults.searchMetadata.performanceMetric && (
							<span className="metadata-item">
								Metric: {searchResults.searchMetadata.performanceMetric}
							</span>
						)}
					</div>
				</div>
			)}

			{error && (
				<div className="search-error-display">
					<span className="error-icon">⚠️</span>
					<span className="error-text">{error}</span>
					<button onClick={handleSearchClick} className="retry-btn">
						Retry Search
					</button>
				</div>
			)}

			{/* Include all the existing styles plus new ones */}
			<style jsx>{`
                .search-interface-container {
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 20px;
                    background: white;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    width: 100%;
                    box-sizing: border-box;
                }

                .search-terms-display {
                    margin-bottom: 12px;
                    padding: 8px 12px;
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                }

                .search-terms-content {
                    flex: 1;
                    overflow: hidden;
                }

                .current-search-term {
                    font-size: 14px;
                    color: #374151;
                    line-height: 1.4;
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 4px;
                }

                .search-term {
                    display: inline-flex;
                    gap: 4px;
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }

                .term-include {
                    background: #dcfce7;
                    color: #166534;
                    border: 1px solid #bbf7d0;
                }

                .term-exclude {
                    background: #fee2e2;
                    color: #991b1b;
                    border: 1px solid #fecaca;
                }

                .term-remove-btn {
                    margin-left: 4px;
                    background: none;
                    border: none;
                    color: inherit;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    padding: 2px;
                    border-radius: 2px;
                    transition: background-color 0.2s;
                }

                .term-remove-btn:hover {
                    background: rgba(0, 0, 0, 0.1);
                }

                .btn-clear-all-inline {
                    padding: 4px 12px;
                    background: #ef4444;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                .btn-clear-all-inline:hover {
                    background: #dc2626;
                }

                .search-input-row {
                    display: flex;
                    align-items: flex-end;
                    gap: 12px;
                    margin-bottom: 16px;
                    flex-wrap: wrap;
                }

                .search-input-wrapper {
                    position: relative;
                    flex: 1;
                    min-width: 200px;
                    display: flex;
                    align-items: center;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    background: white;
                    padding: 0;
                    overflow: hidden;
                }

                .search-icon-container {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 2;
                    pointer-events: none;
                    color: #9ca3af;
                }

                .search-input {
                    flex: 1;
                    border: none;
                    outline: none;
                    padding: 12px 16px 12px 44px !important;
                    font-size: 14px;
                    background: transparent;
                    color: #374151;
                    text-align: left !important;
                    width: 100%;
                    box-sizing: border-box;
                }

                .search-input::placeholder {
                    color: #9ca3af;
                    text-align: left !important;
                }

                .search-input-wrapper:focus-within {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .term-type-buttons {
                    display: flex;
                    gap: 4px;
                    margin: 0 8px;
                    flex-shrink: 0;
                }

                .term-type-btn {
                    padding: 6px 12px;
                    border: 1px solid #d1d5db;
                    border-radius: 4px;
                    background: white;
                    color: #6b7280;
                    font-size: 12px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                    white-space: nowrap;
                }

                .include-btn {
                    background: #10b981;
                    color: white;
                    border-color: #10b981;
                }

                .include-btn:hover:not(:disabled) {
                    background: #059669;
                    border-color: #059669;
                }

                .exclude-btn {
                    background: #ef4444;
                    color: white;
                    border-color: #ef4444;
                }

                .exclude-btn:hover:not(:disabled) {
                    background: #dc2626;
                    border-color: #dc2626;
                }

                .term-type-btn:disabled {
                    background: #f3f4f6;
                    color: #9ca3af;
                    border-color: #d1d5db;
                    cursor: not-allowed;
                }

                .tier-input-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    min-width: 140px;
                    max-width: 200px;
                    position: relative;
                    flex-shrink: 0;
                }

                .tier-label {
                    font-size: 12px;
                    font-weight: 500;
                    color: #6b7280;
                    margin-bottom: 4px;
                    white-space: nowrap;
                }

                .tier-dropdown-container {
                    position: relative;
                    width: 100%;
                    min-width: 180px;
                }

                .tier-dropdown-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .tier-simple-select {
                    flex: 1;
                    padding: 12px;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    background: white;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-size: 13px;
                    color: #374151;
                    font-weight: 600;
                }

                .tier-simple-select:hover {
                    border-color: #9ca3af;
                    background: #f9fafb;
                }

                .tier-simple-select:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .info-icon-container {
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: help;
                }

                .info-icon {
                    font-size: 16px;
                    opacity: 0.7;
                    transition: opacity 0.2s ease;
                }

                .info-icon-container:hover .info-icon {
                    opacity: 1;
                }

                .tier-tooltip {
                    position: absolute;
                    top: calc(100% + 8px);
                    right: 0;
                    background: white;
                    border: 1px solid #d1d5db;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    padding: 12px;
                    z-index: 1000;
                    min-width: 320px;
                    max-width: 400px;
                }

                .tooltip-header {
                    text-align: center;
                    margin-bottom: 12px;
                    font-size: 14px;
                    color: #374151;
                    border-bottom: 1px solid #e5e7eb;
                    padding-bottom: 8px;
                }

                .tooltip-content {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .tooltip-tier {
                    padding: 8px;
                    border-radius: 6px;
                    border: 1px solid #e5e7eb;
                }

                .tooltip-tier.warning {
                    background: #fefce8;
                    border-color: #fde047;
                }

                .tooltip-tier-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 4px;
                }

                .tooltip-tier-label {
                    font-size: 13px;
                    font-weight: 600;
                    color: #374151;
                }

                .tooltip-tier-score {
                    font-size: 12px;
                    font-weight: 700;
                    color: #059669;
                    background: #dcfce7;
                    padding: 2px 6px;
                    border-radius: 4px;
                }

                .tooltip-tier.warning .tooltip-tier-score {
                    color: #d97706;
                    background: #fef3c7;
                }

                .tooltip-tier-meaning {
                    font-size: 11px;
                    color: #6b7280;
                    line-height: 1.4;
                    display: flex;
                    align-items: flex-start;
                    gap: 4px;
                }

                .tooltip-tier.warning .tooltip-tier-meaning {
                    color: #92400e;
                }

                .warning-indicator {
                    font-size: 10px;
                    flex-shrink: 0;
                    margin-top: 1px;
                }

                .search-button-container {
                    display: flex;
                    align-items: flex-end;
                    flex-shrink: 0;
                }

                .search-button {
                    padding: 12px 20px;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    white-space: nowrap;
                }

                .search-button:hover:not(:disabled) {
                    background: #2563eb;
                }

                .search-button:disabled {
                    background: #d1d5db;
                    color: #9ca3af;
                    cursor: not-allowed;
                }

                .loading-spinner-small {
                    width: 16px;
                    height: 16px;
                    border: 2px solid transparent;
                    border-top: 2px solid currentColor;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    to {
                        transform: rotate(360deg);
                    }
                }

                .logic-builder {
                    margin: 16px 0;
                    padding: 16px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                }

                .logic-builder-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                }

                .logic-builder-header h4 {
                    margin: 0;
                    font-size: 14px;
                    font-weight: 600;
                    color: #374151;
                }

                .logic-helper {
                    font-size: 12px;
                    color: #6b7280;
                }

                .logic-chain {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 12px;
                }

                .logic-item-group {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .logic-term-button {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    padding: 8px 12px;
                    background: white;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    font-size: 13px;
                    color: #374151;
                }

                .logic-term-button.include-term {
                    border-color: #10b981;
                    background: #f0fdfa;
                }

                .logic-term-button.exclude-term {
                    border-color: #ef4444;
                    background: #fef2f2;
                }

                .term-prefix {
                    font-weight: 700;
                    font-size: 14px;
                }

                .include-term .term-prefix {
                    color: #059669;
                }

                .exclude-term .term-prefix {
                    color: #dc2626;
                }

                .term-text {
                    font-weight: 500;
                }

                .term-score {
                    color: #6b7280;
                    font-size: 12px;
                }

                .logic-operator {
                    padding: 6px 12px;
                    border: none;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                    text-transform: uppercase;
                }

                .logic-operator.and {
                    background: #dbeafe;
                    color: #1e40af;
                }

                .logic-operator.and:hover {
                    background: #bfdbfe;
                }

                .logic-operator.or {
                    background: #fed7aa;
                    color: #c2410c;
                }

                .logic-operator.or:hover {
                    background: #fdba74;
                }

                .logic-operator.not {
                    background: #fee2e2;
                    color: #dc2626;
                }

                .logic-operator.not:hover {
                    background: #fecaca;
                }

                .logic-explanation {
                    padding: 8px 12px;
                    background: #f1f5f9;
                    border: 1px solid #cbd5e1;
                    border-radius: 4px;
                    margin-top: 8px;
                }

                .logic-explanation-text {
                    font-size: 12px;
                    color: #475569;
                    line-height: 1.4;
                }

                .search-reminder {
                    color: #3b82f6;
                    font-weight: 500;
                }

                .quick-filters {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                    padding-top: 12px;
                    border-top: 1px solid #e5e7eb;
                    align-items: center;
                }

                .quick-filter {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    padding: 4px 8px;
                    background: #f3f4f6;
                    color: #6b7280;
                    border-radius: 4px;
                    font-size: 11px;
                    border: 1px solid #e5e7eb;
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                .filter-results {
                    background: #dcfce7;
                    color: #166534;
                    border: 1px solid #bbf7d0;
                    font-weight: 500;
                }

                .filter-terms {
                    background: #e0e7ff;
                    color: #3730a3;
                    border: 1px solid #c7d2fe;
                    font-weight: 500;
                }

                .filter-tier {
                    background: #e0f2fe;
                    color: #0c4a6e;
                    border: 1px solid #0891b2;
                    font-weight: 500;
                }

                .search-stage-indicator {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-top: 12px;
                    padding: 8px 12px;
                    background: #fef3c7;
                    border: 1px solid #f59e0b;
                    border-radius: 6px;
                }

                .tag {
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                }

                .tag-orange {
                    background: #fed7aa;
                    color: #9a3412;
                }

                .tag-blue {
                    background: #bfdbfe;
                    color: #1e40af;
                }

                .search-stage-text {
                    flex: 1;
                    font-size: 13px;
                    color: #92400e;
                    line-height: 1.4;
                }

                .demographic-probabilities-indicator {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-top: 12px;
                    padding: 8px 12px;
                    background: #e0f2fe;
                    border: 1px solid #0891b2;
                    border-radius: 6px;
                }

                .demographic-probability-text {
                    font-size: 12px;
                    color: #0c4a6e;
                    font-weight: 500;
                }

                .search-metadata-display {
                    margin-top: 8px;
                    padding: 8px 12px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 4px;
                }

                .metadata-items {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 12px;
                }

                .metadata-item {
                    font-size: 11px;
                    color: #64748b;
                    padding: 2px 6px;
                    background: white;
                    border-radius: 3px;
                }

                .search-error-display {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-top: 12px;
                    padding: 10px 12px;
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 6px;
                }

                .error-icon {
                    color: #dc2626;
                }

                .error-text {
                    flex: 1;
                    font-size: 13px;
                    color: #991b1b;
                }

                .retry-btn {
                    padding: 4px 8px;
                    background: #dc2626;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-size: 11px;
                    cursor: pointer;
                }

                .retry-btn:hover {
                    background: #b91c1c;
                }

                /* Mobile Responsiveness */
                @media (max-width: 768px) {
                    .search-interface-container {
                        padding: 16px;
                    }

                    .search-input-row {
                        flex-direction: column;
                        gap: 12px;
                        align-items: stretch;
                    }

                    .search-input-wrapper {
                        width: 100%;
                        min-width: unset;
                    }

                    .search-input {
                        font-size: 16px;
                        padding: 12px 16px 12px 44px !important;
                    }

                    .tier-input-container {
                        width: 100%;
                        min-width: unset;
                        max-width: none;
                        align-items: stretch;
                    }

                    .tier-dropdown-container {
                        min-width: auto;
                    }

                    .search-button-container {
                        width: 100%;
                    }

                    .search-button {
                        width: 100%;
                        justify-content: center;
                        padding: 14px 20px;
                    }

                    .tier-tooltip {
                        position: fixed;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        right: auto;
                        bottom: auto;
                        width: calc(100vw - 32px);
                        max-width: 350px;
                        max-height: 60vh;
                        overflow-y: auto;
                    }

                    .tier-tooltip::before,
                    .tier-tooltip::after {
                        display: none;
                    }

                    .logic-chain {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 8px;
                    }

                    .logic-item-group {
                        flex-direction: column;
                        gap: 6px;
                    }

                    .logic-operator {
                        align-self: center;
                        width: auto;
                    }
                }
            `}</style>
		</div>
	);
};
